import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { productsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Package, 
  LogOut, 
  Trash2, 
  Edit,
  ShoppingBag,
  RefreshCw,
  Plus,
  X,
  Upload,
  Image as ImageIcon
} from 'lucide-react';
import { toast } from 'sonner';

const categories = [
  { value: 'standart', label: 'Стандарт' },
  { value: 'standart-plus', label: 'Стандарт+' },
  { value: 'bogatyr', label: 'Богатырь' },
  { value: 'premium', label: 'Премиум' },
];

const AdminProductsPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    category: 'standart',
    price: '',
    oldPrice: '',
    discount: 0,
    size: { width: 3, length: 4, height: 2.1 },
    frame: '',
    polycarbonate: '4 мм сотовый с UV-защитой',
    polycarbonateOptions: [
      { thickness: '4мм', price_modifier: 0, description: 'Стандарт (4мм, 0.52 кг/м²)' },
      { thickness: '6мм', price_modifier: 2000, description: 'Усиленный (6мм, 0.6 кг/м²)' },
      { thickness: '8мм', price_modifier: 4000, description: 'Премиум (8мм, 0.7 кг/м²)' }
    ],
    warranty: 3,
    inStock: true,
    isHit: false,
    isNew: false,
    step: '',
    images: [],
    description: '',
    features: [],
  });
  const [uploadingImage, setUploadingImage] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/admin/login');
      return;
    }
    loadProducts();
  }, [user, navigate]);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const data = await productsAPI.getAll();
      setProducts(data);
    } catch (error) {
      toast.error('Ошибка загрузки товаров');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      slug: product.slug,
      category: product.category,
      price: product.price,
      oldPrice: product.oldPrice || '',
      discount: product.discount,
      size: product.size,
      frame: product.frame,
      polycarbonate: product.polycarbonate,
      polycarbonateOptions: product.polycarbonateOptions || [
        { thickness: '4мм', price_modifier: 0, description: 'Стандарт (4мм, 0.52 кг/м²)' },
        { thickness: '6мм', price_modifier: 2000, description: 'Усиленный (6мм, 0.6 кг/м²)' },
        { thickness: '8мм', price_modifier: 4000, description: 'Премиум (8мм, 0.7 кг/м²)' }
      ],
      warranty: product.warranty,
      inStock: product.inStock,
      isHit: product.isHit,
      isNew: product.isNew,
      step: product.step,
      images: product.images,
      description: product.description,
      features: product.features,
    });
    setShowForm(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот товар?')) {
      return;
    }
    
    try {
      await productsAPI.delete(productId);
      toast.success('Товар удален');
      loadProducts();
    } catch (error) {
      toast.error('Ошибка удаления товара');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        oldPrice: formData.oldPrice ? parseFloat(formData.oldPrice) : null,
        discount: parseInt(formData.discount),
        warranty: parseInt(formData.warranty),
      };

      if (editingProduct) {
        await productsAPI.update(editingProduct.id, productData);
        toast.success('Товар обновлен');
      } else {
        await productsAPI.create(productData);
        toast.success('Товар создан');
      }
      
      resetForm();
      loadProducts();
    } catch (error) {
      toast.error('Ошибка сохранения товара');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!editingProduct) {
      toast.error('Сначала сохраните товар, затем добавьте изображения');
      return;
    }

    try {
      setUploadingImage(true);
      const result = await productsAPI.uploadImage(editingProduct.id, file);
      
      setFormData({
        ...formData,
        images: [...formData.images, result.image_url],
      });
      
      toast.success('Изображение загружено');
      loadProducts();
    } catch (error) {
      toast.error('Ошибка загрузки изображения');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleRemoveImage = (index) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData({ ...formData, images: newImages });
  };

  const addFeature = () => {
    setFormData({
      ...formData,
      features: [...formData.features, ''],
    });
  };

  const updateFeature = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData({ ...formData, features: newFeatures });
  };

  const removeFeature = (index) => {
    const newFeatures = formData.features.filter((_, i) => i !== index);
    setFormData({ ...formData, features: newFeatures });
  };

  const resetForm = () => {
    setShowForm(false);
    setEditingProduct(null);
    setFormData({
      name: '',
      slug: '',
      category: 'standart',
      price: '',
      oldPrice: '',
      discount: 0,
      size: { width: 3, length: 4, height: 2.1 },
      frame: '',
      polycarbonate: '4 мм сотовый с UV-защитой',
      polycarbonateOptions: [
        { thickness: '4мм', price_modifier: 0, description: 'Стандарт (4мм, 0.52 кг/м²)' },
        { thickness: '6мм', price_modifier: 2000, description: 'Усиленный (6мм, 0.6 кг/м²)' },
        { thickness: '8мм', price_modifier: 4000, description: 'Премиум (8мм, 0.7 кг/м²)' }
      ],
      warranty: 3,
      inStock: true,
      isHit: false,
      isNew: false,
      step: '',
      images: [],
      description: '',
      features: [],
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold text-gray-900">Админ-панель</h1>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => navigate('/admin/orders')}
                >
                  <Package className="w-4 h-4 mr-2" />
                  Заказы
                </Button>
                <Button
                  variant="outline"
                  className="border-emerald-600 text-emerald-700"
                  onClick={() => navigate('/admin/products')}
                >
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  Товары
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/admin/gallery')}
                >
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  Галерея
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/admin/reviews')}
                >
                  <Package className="w-4 h-4 mr-2" />
                  Отзывы
                </Button>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">
                {user?.username}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={logout}
              >
                <LogOut className="w-4 h-4 mr-2" />
                Выйти
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-8">
        {!showForm ? (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Товары</CardTitle>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={loadProducts}
                    disabled={loading}
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Обновить
                  </Button>
                  <Button
                    onClick={() => setShowForm(true)}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Добавить товар
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-12">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto text-emerald-600 mb-4" />
                  <p className="text-gray-600">Загрузка товаров...</p>
                </div>
              ) : products.length === 0 ? (
                <div className="text-center py-12">
                  <ShoppingBag className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600">Товаров пока нет</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Название</TableHead>
                        <TableHead>Категория</TableHead>
                        <TableHead>Размер</TableHead>
                        <TableHead>Цена</TableHead>
                        <TableHead>Статус</TableHead>
                        <TableHead className="text-right">Действия</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {products.map((product) => (
                        <TableRow key={product.id}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-3">
                              {product.images[0] && (
                                <img 
                                  src={product.images[0]} 
                                  alt={product.name}
                                  className="w-12 h-12 object-cover rounded"
                                />
                              )}
                              <div>
                                <p>{product.name}</p>
                                <div className="flex gap-1 mt-1">
                                  {product.isHit && (
                                    <Badge className="bg-orange-100 text-orange-700 text-xs">
                                      ХИТ
                                    </Badge>
                                  )}
                                  {product.isNew && (
                                    <Badge className="bg-blue-100 text-blue-700 text-xs">
                                      НОВИНКА
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>
                            {categories.find(c => c.value === product.category)?.label}
                          </TableCell>
                          <TableCell>
                            {product.size.width}x{product.size.length} м
                          </TableCell>
                          <TableCell className="font-semibold">
                            {product.price.toLocaleString('ru-RU')} ₽
                          </TableCell>
                          <TableCell>
                            <Badge className={product.inStock ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                              {product.inStock ? 'В наличии' : 'Нет в наличии'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(product)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(product.id)}
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>
                  {editingProduct ? 'Редактировать товар' : 'Добавить товар'}
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={resetForm}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Название *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="slug">Slug (URL) *</Label>
                    <Input
                      id="slug"
                      value={formData.slug}
                      onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="category">Категория *</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData({ ...formData, category: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="price">Цена *</Label>
                    <Input
                      id="price"
                      type="number"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="oldPrice">Старая цена</Label>
                    <Input
                      id="oldPrice"
                      type="number"
                      value={formData.oldPrice}
                      onChange={(e) => setFormData({ ...formData, oldPrice: e.target.value })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="warranty">Гарантия (лет) *</Label>
                    <Input
                      id="warranty"
                      type="number"
                      value={formData.warranty}
                      onChange={(e) => setFormData({ ...formData, warranty: e.target.value })}
                      required
                    />
                  </div>
                </div>

                {/* Size */}
                <div>
                  <Label className="mb-2 block">Размеры *</Label>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="width" className="text-xs">Ширина (м)</Label>
                      <Input
                        id="width"
                        type="number"
                        step="0.1"
                        value={formData.size.width}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          size: { ...formData.size, width: parseFloat(e.target.value) }
                        })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="length" className="text-xs">Длина (м)</Label>
                      <Input
                        id="length"
                        type="number"
                        step="0.1"
                        value={formData.size.length}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          size: { ...formData.size, length: parseFloat(e.target.value) }
                        })}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="height" className="text-xs">Высота (м)</Label>
                      <Input
                        id="height"
                        type="number"
                        step="0.1"
                        value={formData.size.height}
                        onChange={(e) => setFormData({ 
                          ...formData, 
                          size: { ...formData.size, height: parseFloat(e.target.value) }
                        })}
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Technical specs */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="frame">Каркас *</Label>
                    <Input
                      id="frame"
                      value={formData.frame}
                      onChange={(e) => setFormData({ ...formData, frame: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="step">Шаг дуг *</Label>
                    <Input
                      id="step"
                      value={formData.step}
                      onChange={(e) => setFormData({ ...formData, step: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="polycarbonate">Поликарбонат *</Label>
                    <Input
                      id="polycarbonate"
                      value={formData.polycarbonate}
                      onChange={(e) => setFormData({ ...formData, polycarbonate: e.target.value })}
                      required
                    />
                  </div>
                </div>

                {/* Polycarbonate Options */}
                <div className="space-y-2 md:col-span-2">
                  <div className="flex items-center justify-between">
                    <Label>Варианты поликарбоната и цены</Label>
                  </div>
                  <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                    {formData.polycarbonateOptions.map((option, index) => (
                      <div key={index} className="grid grid-cols-3 gap-3 items-center p-3 bg-white rounded border">
                        <div>
                          <Label className="text-xs">Толщина</Label>
                          <Input
                            value={option.thickness}
                            onChange={(e) => {
                              const newOptions = [...formData.polycarbonateOptions];
                              newOptions[index].thickness = e.target.value;
                              setFormData({ ...formData, polycarbonateOptions: newOptions });
                            }}
                            placeholder="4мм"
                            disabled
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Доплата (₽)</Label>
                          <Input
                            type="number"
                            value={option.price_modifier}
                            onChange={(e) => {
                              const newOptions = [...formData.polycarbonateOptions];
                              newOptions[index].price_modifier = parseFloat(e.target.value) || 0;
                              setFormData({ ...formData, polycarbonateOptions: newOptions });
                            }}
                            placeholder="0"
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Описание</Label>
                          <Input
                            value={option.description}
                            onChange={(e) => {
                              const newOptions = [...formData.polycarbonateOptions];
                              newOptions[index].description = e.target.value;
                              setFormData({ ...formData, polycarbonateOptions: newOptions });
                            }}
                            placeholder="Описание"
                            className="mt-1"
                          />
                        </div>
                      </div>
                    ))}
                    <p className="text-xs text-gray-500 mt-2">
                      💡 Цена товара ({formData.price}₽) это базовая цена для поликарбоната 4мм. 
                      Для других вариантов будет добавлена доплата.
                    </p>
                  </div>
                </div>


                {/* Description */}
                <div className="space-y-2">
                  <Label htmlFor="description">Описание *</Label>
                  <Textarea
                    id="description"
                    rows={4}
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    required
                  />
                </div>

                {/* Features */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Характеристики</Label>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addFeature}
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Добавить
                    </Button>
                  </div>
                  {formData.features.map((feature, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={feature}
                        onChange={(e) => updateFeature(index, e.target.value)}
                        placeholder="Характеристика"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeFeature(index)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>

                {/* Images */}
                <div className="space-y-2">
                  <Label>Изображения</Label>
                  {editingProduct && (
                    <div className="mb-2">
                      <Input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={uploadingImage}
                      />
                      {uploadingImage && (
                        <p className="text-sm text-gray-600 mt-1">Загрузка...</p>
                      )}
                    </div>
                  )}
                  {formData.images.length > 0 && (
                    <div className="grid grid-cols-4 gap-4">
                      {formData.images.map((image, index) => (
                        <div key={index} className="relative group">
                          <img 
                            src={image} 
                            alt={`Product ${index + 1}`}
                            className="w-full h-24 object-cover rounded"
                          />
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => handleRemoveImage(index)}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  {!editingProduct && formData.images.length === 0 && (
                    <p className="text-sm text-gray-500">
                      Сохраните товар, чтобы добавить изображения
                    </p>
                  )}
                </div>

                {/* Flags */}
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.inStock}
                      onChange={(e) => setFormData({ ...formData, inStock: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">В наличии</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.isHit}
                      onChange={(e) => setFormData({ ...formData, isHit: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">ХИТ продаж</span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.isNew}
                      onChange={(e) => setFormData({ ...formData, isNew: e.target.checked })}
                      className="rounded"
                    />
                    <span className="text-sm">Новинка</span>
                  </label>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  <Button
                    type="submit"
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    {editingProduct ? 'Сохранить изменения' : 'Создать товар'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={resetForm}
                  >
                    Отмена
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminProductsPage;
